import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  Avatar,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Alert,
  InputAdornment,
  Tooltip,
  Divider,
  Grid,
  Autocomplete
} from '@mui/material';
import {
  Share as ShareIcon,
  Link as LinkIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  Public as PublicIcon,
  QrCode as QrCodeIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import QRCode from 'qrcode';

// Stores
import usePDFStore from '../../stores/pdfStore';
import useUserStore from '../../stores/userStore';
import useNotificationStore from '../../stores/notificationStore';

const PDFShare = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentPdf, loading, fetchPDF, sharePDF, unsharePDF } = usePDFStore();
  const { users, fetchUsers } = useUserStore();
  const { showSuccess, showError } = useNotificationStore();
  
  const [activeTab, setActiveTab] = useState(0);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [shareRole, setShareRole] = useState('viewer');
  const [sharePermissions, setSharePermissions] = useState({
    canDownload: true,
    canPrint: true,
    canCopy: false,
    canShare: false
  });
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [editingShare, setEditingShare] = useState(null);
  
  // Load PDF and users
  useEffect(() => {
    if (id) {
      fetchPDF(id);
      fetchUsers();
    }
  }, [id]);
  
  // Generate QR code
  useEffect(() => {
    if (currentPdf?.shortCode) {
      const url = `${window.location.origin}/p/${currentPdf.shortCode}`;
      QRCode.toDataURL(url, { width: 200 }, (err, dataUrl) => {
        if (!err) setQrCodeUrl(dataUrl);
      });
    }
  }, [currentPdf]);
  
  // Get PDF URLs
  const getPDFUrls = () => {
    if (!currentPdf) return {};
    const baseUrl = window.location.origin;
    return {
      short: `${baseUrl}/p/${currentPdf.shortCode}`,
      long: `${baseUrl}/pdf/view/${currentPdf._id}`,
      slug: currentPdf.slug ? `${baseUrl}/docs/${currentPdf.slug}` : null
    };
  };
  
  // Copy to clipboard
  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    showSuccess(`${label} copied to clipboard`);
  };
  
  // Handle share
  const handleShare = async () => {
    if (!selectedUser) {
      showError('Please select a user');
      return;
    }
    
    try {
      await sharePDF(currentPdf._id, {
        userId: selectedUser._id,
        role: shareRole,
        permissions: sharePermissions
      });
      
      showSuccess('PDF shared successfully');
      setShareDialogOpen(false);
      setSelectedUser(null);
      setShareRole('viewer');
      setSharePermissions({
        canDownload: true,
        canPrint: true,
        canCopy: false,
        canShare: false
      });
      
      // Refresh PDF data
      fetchPDF(id);
    } catch (error) {
      showError('Failed to share PDF');
    }
  };
  
  // Handle unshare
  const handleUnshare = async (userId) => {
    if (window.confirm('Remove access for this user?')) {
      try {
        await unsharePDF(currentPdf._id, userId);
        showSuccess('Access removed');
        fetchPDF(id);
      } catch (error) {
        showError('Failed to remove access');
      }
    }
  };
  
  // Handle edit share
  const handleEditShare = (share) => {
    setEditingShare(share);
    setSelectedUser(share.userId);
    setShareRole(share.role);
    setSharePermissions(share.permissions);
    setShareDialogOpen(true);
  };
  
  // Download QR code
  const downloadQRCode = () => {
    const link = document.createElement('a');
    link.download = `${currentPdf.slug || currentPdf._id}-qr.png`;
    link.href = qrCodeUrl;
    link.click();
  };
  
  if (!currentPdf) {
    return null;
  }
  
  const urls = getPDFUrls();
  
  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Share: {currentPdf.title}
      </Typography>
      
      <Paper sx={{ mt: 3 }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
          <Tab label="Share Links" />
          <Tab label="Share with Users" />
          <Tab label="Embed" />
        </Tabs>
        
        <Box sx={{ p: 3 }}>
          {/* Share Links Tab */}
          {activeTab === 0 && (
            <Grid container spacing={3}>
              {/* Public Access Toggle */}
              <Grid item xs={12}>
                <Alert 
                  severity={currentPdf.isPublic ? 'info' : 'warning'}
                  icon={currentPdf.isPublic ? <PublicIcon /> : <LockIcon />}
                >
                  <Typography variant="subtitle2">
                    {currentPdf.isPublic 
                      ? 'This PDF is public. Anyone with the link can view it.'
                      : 'This PDF is private. Only authenticated users with permissions can view it.'}
                  </Typography>
                </Alert>
              </Grid>
              
              {/* Short URL */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Short URL
                </Typography>
                <TextField
                  fullWidth
                  value={urls.short}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => copyToClipboard(urls.short, 'Short URL')}>
                          <CopyIcon />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              
              {/* Long URL */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Direct URL
                </Typography>
                <TextField
                  fullWidth
                  value={urls.long}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => copyToClipboard(urls.long, 'Direct URL')}>
                          <CopyIcon />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              
              {/* Custom Slug URL */}
              {urls.slug && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Custom URL
                  </Typography>
                  <TextField
                    fullWidth
                    value={urls.slug}
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => copyToClipboard(urls.slug, 'Custom URL')}>
                            <CopyIcon />
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
              )}
              
              {/* QR Code */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  QR Code
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  {qrCodeUrl && (
                    <img src={qrCodeUrl} alt="QR Code" style={{ border: '1px solid #e0e0e0' }} />
                  )}
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Scan this code to quickly access the PDF on mobile devices
                    </Typography>
                    <Button
                      startIcon={<DownloadIcon />}
                      onClick={downloadQRCode}
                      variant="outlined"
                      size="small"
                    >
                      Download QR Code
                    </Button>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          )}
          
          {/* Share with Users Tab */}
          {activeTab === 1 && (
            <>
              <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">
                  Shared with
                </Typography>
                <Button
                  startIcon={<AddIcon />}
                  variant="contained"
                  onClick={() => setShareDialogOpen(true)}
                >
                  Share with User
                </Button>
              </Box>
              
              <List>
                {/* Owner */}
                <ListItem>
                  <ListItemAvatar>
                    <Avatar>
                      {currentPdf.createdBy?.firstName?.[0]}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={`${currentPdf.createdBy?.firstName} ${currentPdf.createdBy?.lastName}`}
                    secondary={currentPdf.createdBy?.email}
                  />
                  <Chip label="Owner" color="primary" size="small" />
                </ListItem>
                
                <Divider />
                
                {/* Shared users */}
                {currentPdf.sharedWith?.map((share) => (
                  <ListItem key={share.userId._id}>
                    <ListItemAvatar>
                      <Avatar>
                        {share.userId.firstName?.[0]}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={`${share.userId.firstName} ${share.userId.lastName}`}
                      secondary={
                        <Box>
                          <Typography variant="caption" display="block">
                            {share.userId.email}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                            {share.permissions.canDownload && <Chip label="Download" size="small" variant="outlined" />}
                            {share.permissions.canPrint && <Chip label="Print" size="small" variant="outlined" />}
                            {share.permissions.canCopy && <Chip label="Copy" size="small" variant="outlined" />}
                            {share.permissions.canShare && <Chip label="Share" size="small" variant="outlined" />}
                          </Box>
                        </Box>
                      }
                    />
                    <Chip label={share.role} size="small" sx={{ mr: 1 }} />
                    <ListItemSecondaryAction>
                      <IconButton onClick={() => handleEditShare(share)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton onClick={() => handleUnshare(share.userId._id)}>
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </>
          )}
          
          {/* Embed Tab */}
          {activeTab === 2 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Alert severity="info">
                  Embed this PDF in your website or application using an iframe
                </Alert>
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Embed Code
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  value={`<iframe src="${urls.short}" width="100%" height="600" frameborder="0"></iframe>`}
                  InputProps={{
                    readOnly: true,
                    sx: { fontFamily: 'monospace' }
                  }}
                />
                <Button
                  startIcon={<CopyIcon />}
                  onClick={() => copyToClipboard(
                    `<iframe src="${urls.short}" width="100%" height="600" frameborder="0"></iframe>`,
                    'Embed code'
                  )}
                  sx={{ mt: 1 }}
                >
                  Copy Embed Code
                </Button>
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Preview
                </Typography>
                <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <iframe
                    src={urls.short}
                    width="100%"
                    height="400"
                    style={{ border: '1px solid #e0e0e0' }}
                    title="PDF Preview"
                  />
                </Paper>
              </Grid>
            </Grid>
          )}
        </Box>
      </Paper>
      
      {/* Share Dialog */}
      <Dialog open={shareDialogOpen} onClose={() => setShareDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingShare ? 'Edit Share Settings' : 'Share with User'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Autocomplete
              options={users.filter(u => 
                u._id !== currentPdf.createdBy._id && 
                !currentPdf.sharedWith?.some(s => s.userId._id === u._id)
              )}
              getOptionLabel={(option) => `${option.firstName} ${option.lastName} (${option.email})`}
              value={selectedUser}
              onChange={(e, value) => setSelectedUser(value)}
              disabled={!!editingShare}
              renderInput={(params) => (
                <TextField {...params} label="Select User" fullWidth />
              )}
              sx={{ mb: 3 }}
            />
            
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Role</InputLabel>
              <Select
                value={shareRole}
                onChange={(e) => setShareRole(e.target.value)}
                label="Role"
              >
                <MenuItem value="viewer">Viewer</MenuItem>
                <MenuItem value="editor">Editor</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>
            
            <Typography variant="subtitle2" gutterBottom>
              Permissions
            </Typography>
            <Box sx={{ pl: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={sharePermissions.canDownload}
                    onChange={(e) => setSharePermissions({
                      ...sharePermissions,
                      canDownload: e.target.checked
                    })}
                  />
                }
                label="Can download PDF"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={sharePermissions.canPrint}
                    onChange={(e) => setSharePermissions({
                      ...sharePermissions,
                      canPrint: e.target.checked
                    })}
                  />
                }
                label="Can print PDF"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={sharePermissions.canCopy}
                    onChange={(e) => setSharePermissions({
                      ...sharePermissions,
                      canCopy: e.target.checked
                    })}
                  />
                }
                label="Can copy content"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={sharePermissions.canShare}
                    onChange={(e) => setSharePermissions({
                      ...sharePermissions,
                      canShare: e.target.checked
                    })}
                  />
                }
                label="Can share with others"
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShareDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleShare} 
            variant="contained"
            disabled={!selectedUser && !editingShare}
          >
            {editingShare ? 'Update' : 'Share'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PDFShare;